
import { useState } from 'react';

export default function CoworkingPartner() {
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

  const favyFeatures = [
    {
      icon: 'ri-map-pin-line',
      title: '新宿駅徒歩5分',
      desc: '新宿三丁目駅徒歩2分、JR新宿駅徒歩5分の抜群のアクセス',
    },
    {
      icon: 'ri-time-line',
      title: '9:00〜23:00',
      desc: '長い営業時間で朝活から夜の作業まで柔軟に対応',
    },
    {
      icon: 'ri-building-line',
      title: '法人登記・住所利用可能',
      desc: 'アドレスプランなら法人登記や住所利用にも対応',
    },
    {
      icon: 'ri-restaurant-line',
      title: 'フードホール直結',
      desc: '5Fのフードホールでコーヒーやグルメを楽しみながら仕事',
    },
  ];

  return (
    <section className="py-20 sm:py-28 md:py-36 bg-gradient-to-b from-white to-stone-50" aria-labelledby="partner-heading">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-14 sm:mb-18">
          <span className="inline-block px-4 py-1.5 bg-emerald-500/10 text-emerald-600 text-xs tracking-[0.15em] rounded-full mb-5">
            PARTNER
          </span>
          <h2
            id="partner-heading"
            className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black tracking-[0.1em] mb-4"
          >
            パートナー紹介
          </h2>
          <p className="text-sm text-black/50 font-extralight tracking-wide max-w-lg mx-auto">
            MeVER Chairの体験を支えるパートナーシップ
          </p>
        </div>

        {/* Main Partner Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-stone-200/60 shadow-sm overflow-hidden">
          {/* Top: Logo & Company Info */}
          <div className="flex flex-col lg:flex-row">
            {/* Logo Area */}
            <div className="lg:w-2/5 bg-gradient-to-br from-stone-50 to-stone-100/50 flex flex-col items-center justify-center p-10 sm:p-14 lg:p-16 border-b lg:border-b-0 lg:border-r border-stone-200/60">
              <div className="w-56 h-24 sm:w-64 sm:h-28 flex items-center justify-center mb-6">
                <img
                  src="https://storage.googleapis.com/studio-design-asset-files/projects/JpOLYg6xqQ/s-300x130_367ba4a5-5c27-46d4-b215-cc89feb938c3.svg"
                  alt="favy ロゴ"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-xs text-black/40 font-extralight tracking-[0.15em] mb-6">
                株式会社favy
              </p>
              <div className="flex gap-3">
                <a
                  href="https://favy.info/coworking"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-black text-white text-xs font-light tracking-[0.1em] hover:bg-black/80 transition-colors whitespace-nowrap rounded-lg cursor-pointer"
                  aria-label="favy公式サイトを見る（外部サイト）"
                >
                  公式サイト
                  <i className="ri-external-link-line ml-1.5 w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true"></i>
                </a>
                <a
                  href="https://www.instagram.com/favy_coworkingspace/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-stone-100 text-black/70 text-xs font-light tracking-[0.1em] hover:bg-stone-200 transition-colors whitespace-nowrap rounded-lg cursor-pointer"
                  aria-label="favy Instagram（外部サイト）"
                >
                  <i className="ri-instagram-line mr-1.5 w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true"></i>
                  Instagram
                </a>
              </div>
            </div>

            {/* Description Area */}
            <div className="lg:w-3/5 p-8 sm:p-10 lg:p-14 flex flex-col justify-center">
              <h3 className="text-lg sm:text-xl font-light text-black tracking-[0.08em] mb-2">
                コワーキングスペース by favy
              </h3>
              <p className="text-xs text-emerald-600 font-light tracking-[0.1em] mb-5">
                新宿区新宿3丁目15-11 アドホック新宿ビル 4F
              </p>
              <p className="text-sm text-black/60 font-extralight leading-[1.9] tracking-wide mb-5">
                『コワーキングスペース by favy』は、法人個人を問わず利用可能な、全席フリーアドレスのコワーキングスペースです（法人登記・住所利用可能）。新宿駅5分の便利な立地と、長い営業時間、充実した設備で、<strong className="font-normal text-black/80">「好きを仕事に」</strong>する人を増やします。
              </p>
              <p className="text-sm text-black/60 font-extralight leading-[1.9] tracking-wide mb-6">
                一時利用、継続利用など、ニーズに合わせたプランが揃っています。5Fにはフードホールも併設されており、ハンドドリップコーヒーやグルメを楽しみながら仕事ができる、他にはない環境です。
              </p>

              {/* MeVER Partnership Badge */}
              <div className="bg-emerald-50/80 border border-emerald-100 rounded-xl p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/10 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-shake-hands-line text-emerald-600 text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-emerald-700 tracking-[0.08em] mb-1">
                      MeVER × favy パートナーシップ
                    </p>
                    <p className="text-xs text-emerald-600/70 font-extralight leading-relaxed">
                      MeVER Chairを実際に体験いただける唯一のコワーキングスペースです。購入前に座り心地や機能をお試しいただけます。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom: Key Features */}
          <div className="border-t border-stone-200/60">
            <div className="grid grid-cols-2 lg:grid-cols-4">
              {favyFeatures.map((feature, idx) => (
                <div
                  key={idx}
                  className={`p-6 sm:p-8 text-center transition-colors duration-300 cursor-default ${
                    hoveredFeature === idx ? 'bg-emerald-50/50' : 'bg-transparent'
                  } ${idx < 3 ? 'border-r border-stone-200/60' : ''} ${idx < 2 ? 'border-b lg:border-b-0 border-stone-200/60' : ''}`}
                  onMouseEnter={() => setHoveredFeature(idx)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <div className="w-9 h-9 flex items-center justify-center mx-auto mb-3">
                    <i
                      className={`${feature.icon} text-xl transition-colors duration-300 ${
                        hoveredFeature === idx ? 'text-emerald-600' : 'text-black/40'
                      }`}
                      aria-hidden="true"
                    ></i>
                  </div>
                  <p className="text-xs sm:text-sm font-light text-black/80 tracking-[0.05em] mb-1.5">
                    {feature.title}
                  </p>
                  <p className="text-[11px] text-black/40 font-extralight leading-relaxed tracking-wide">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Company Info Footer */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-[11px] text-black/35 font-extralight tracking-wide">
          <a
            href="https://favy.co.jp/#about"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="hover:text-black/60 transition-colors cursor-pointer"
          >
            会社概要
            <i className="ri-external-link-line ml-1" aria-hidden="true"></i>
          </a>
          <span className="hidden sm:inline text-black/15">|</span>
          <a
            href="https://info.favy.jp/security.html"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="hover:text-black/60 transition-colors cursor-pointer"
          >
            情報セキュリティ方針
            <i className="ri-external-link-line ml-1" aria-hidden="true"></i>
          </a>
          <span className="hidden sm:inline text-black/15">|</span>
          <span>copyright© favy Inc.</span>
        </div>
      </div>
    </section>
  );
}
